<template>
  <flex-column>
    <flex-body ref="scroll">
      <div class="picker-grid">
        <div class="row" v-for="(row, rowIndex) in rows" :key="rowIndex">
          <div
            class="cell"
            :style="style(item, row)"
            :class="[
              item.height > item.width ? 'portrait' : 'landscape',
              { selected: isSelected(item) },
            ]"
            v-for="item in row"
            :key="item._id"
            @click="toggle(item)"
          >
            <img
              :src="src(item)"
              :width="item.width"
              :height="item.height"
              loading="lazy"
            />
            <div class="overlay">
              <div class="sheet" />
              <div class="check" :class="{ checked: isSelected(item) }">
                <ux-icon
                  :icon="
                    isSelected(item)
                      ? 'fa-fw fa-circle-check'
                      : 'fa-fw far fa-circle'
                  "
                />
              </div>
              <div v-if="isVideo(item)" class="play">
                <ux-icon icon="fa-fw fa-play" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </flex-body>
  </flex-column>
</template>
<script>
export default {
  props: {
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    selection: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  emits: ["select:item:toggle", "click:item"],
  data() {
    return {
      containerWidth: 0,
    };
  },
  mounted() {
    this._resizeObserver = new ResizeObserver((entries) => {
      this.containerWidth = entries[0].contentRect.width;
    });
    this.$nextTick(() => {
      if (this.$refs.scroll?.$el) {
        this._resizeObserver.observe(this.$refs.scroll.$el);
      }
    });
  },
  beforeUnmount() {
    this._resizeObserver?.disconnect();
  },
  methods: {
    toggle(item) {
      this.$emit("select:item:toggle", item);
    },
    isVideo(item) {
      return (item.meta?.type || item.meta?.definition) === "video";
    },
    isSelected(item) {
      var self = this;
      var itemID = item._id || item.id;
      return self.selection.some(function (r) {
        var rid = r._id || r.id;
        return r == item || rid == itemID;
      });
    },
    rowHeight(row) {
      const gap = 8;
      const totalGaps = (row.length - 1) * gap;
      const available = this.containerWidth - totalGaps;
      const sumAR = row.reduce(
        (s, item) => s + (item.width || 1) / (item.height || 1),
        0,
      );
      return available / sumAR;
    },
    style(item, row) {
      const ar = (item.width || 1) / (item.height || 1);
      const height = this.rowHeight(row);
      const isLastRow = this.rows[this.rows.length - 1] === row;
      const backgroundColor = this.isVideo(item)
        ? "#000"
        : (item.fileMeta?.colors?.colors || [])[0] ||
          "color-mix(in lab, currentColor 5%, transparent)";

      // For the last row, cap height at target to avoid oversized images
      const finalHeight =
        isLastRow && row.length < 3 ? Math.min(height, 250) : height;

      return {
        backgroundColor,
        flex: `${ar} 0 0px`,
        height: `${finalHeight}px`,
      };
    },
    src(item) {
      return this.$sdk.files.mediaUrl("image", item, { w: 300 });
    },
  },
  computed: {
    rows() {
      const containerWidth = this.containerWidth;
      if (!containerWidth || !this.items?.length) return [];

      const targetHeight = 250;
      const gap = 8;
      const items = this.items;
      const rows = [];
      let currentRow = [];
      let currentRowAspectSum = 0;

      for (const item of items) {
        const ar = (item.width || 1) / (item.height || 1);
        currentRow.push(item);
        currentRowAspectSum += ar;

        const totalGaps = (currentRow.length - 1) * gap;
        const neededWidth = currentRowAspectSum * targetHeight + totalGaps;

        if (neededWidth >= containerWidth) {
          rows.push(currentRow);
          currentRow = [];
          currentRowAspectSum = 0;
        }
      }

      // Last row
      if (currentRow.length) {
        rows.push(currentRow);
      }

      return rows;
    },
  },
};
</script>
<style lang="scss" scoped>
.picker-grid {
  padding: 1px;
}

.row {
  display: flex;
  flex-wrap: nowrap;
  gap: 1px;
  margin: 0 0 1px 0;
}

.cell {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  outline: 0.6em solid transparent;
  outline-offset: -0.6em;
  transition: outline 0.25s;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: all 0.3s;
  }

  &:hover {
    .sheet {
      opacity: 0.35;
    }
    .check {
      opacity: 1;
    }
  }

  &.selected {
    outline: 0.6em solid var(--primary);

    .check {
      opacity: 1;
    }
  }
}

.overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  color: #fff;
  z-index: 2;
  overflow: hidden;

  .sheet {
    background: linear-gradient(
      0deg,
      transparent,
      transparent,
      rgba(0, 0, 0, 0.5)
    );
    opacity: 0;
    position: absolute;
    inset: 0;
    transition: opacity 0.25s;
  }

  .check {
    position: absolute;
    top: 0.4em;
    left: 0.5em;
    font-size: 1.5em;
    line-height: 1;
    opacity: 0;
    transition: opacity 0.25s;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);

    &.checked {
      color: var(--primary);
      opacity: 1;
    }
  }

  .play {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 2em;
    opacity: 0.85;
    text-shadow: 0 1px 6px rgba(0, 0, 0, 0.8);
  }
}
</style>
